import {CreateError, ResultWithError} from 'packages/errors/errors';
import {get} from 'packages/rest/api';
import {history} from './history';

type ExtraTag = {
    Tag: string;
    Attributes: Record<string, string> | null;
    Content: string;
};

type Response<T> = {
    tags: ExtraTag[];
    meta: T;
    is_404: boolean;
    redirect: string;
};

export async function GetPageMeta<T>(path: string): Promise<ResultWithError<T>> {
    const response = await get<Response<T>>({
        url: `system/meta${path && path === '/' ? `/` : `/${path}`}`,
    });

    if (response[1]) {
        return response;
    } else if (response[0].is_404) {
        return [null, CreateError(404, 'Bad Request')];
    } else if (response[0].redirect.length > 0) {
        history.push(response[0].redirect);
        return [null, CreateError(307, 'Redirect')];
    }

    if (response[0]) {
        response[0].tags.forEach(applyHeadTag);
    }

    return [response[0].meta, null];
}

function applyHeadTag(tag: ExtraTag) {
    if (tag.Tag === 'title') {
        document.title = tag.Content;
        return;
    }

    if (tag.Tag === 'link' && tag.Attributes?.rel === 'canonical') {
        applyCanonical(tag.Attributes.href);
        return;
    }

    if (tag.Tag !== 'meta' || !tag.Attributes) {
        return;
    }

    const selector = getMetaSelector(tag.Attributes);
    const metaTag =
        (selector ? document.head.querySelector<HTMLMetaElement>(selector) : null) ?? document.createElement('meta');

    Object.entries(tag.Attributes).forEach(([key, value]) => {
        metaTag.setAttribute(key, value);
    });

    if (tag.Content.length > 0) {
        metaTag.textContent = tag.Content;
    }

    if (!metaTag.parentElement) {
        document.head.appendChild(metaTag);
    }
}

function applyCanonical(href: string) {
    const link =
        document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ?? document.createElement('link');
    link.rel = 'canonical';
    link.href = href;

    if (!link.parentElement) {
        document.head.appendChild(link);
    }
}

function getMetaSelector(attributes: Record<string, string>): string | null {
    if (attributes.name) {
        return `meta[name="${CSS.escape(attributes.name)}"]`;
    }

    if (attributes.property) {
        return `meta[property="${CSS.escape(attributes.property)}"]`;
    }

    if (attributes['http-equiv']) {
        return `meta[http-equiv="${CSS.escape(attributes['http-equiv'])}"]`;
    }

    return null;
}
